import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, MapPin, Phone, Mail, Clock, Building2,
  Upload, X, Image as ImageIcon, ExternalLink, AlertTriangle
} from "lucide-react";
import { getImageUrl } from "@/utils/imageUrl";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];

const emptyForm = {
  name: "", slug: "", address: "", zip_code: "", city: "", phone: "", email: "",
  image_url: "", map_image_url: "", google_maps_url: "",
  active: true, order: 0,
  show_in_header: true, header_order: 0,
  working_hours: { lunes: "07:00-18:00", martes: "07:00-18:00", miercoles: "07:00-18:00", jueves: "07:00-18:00", viernes: "07:00-18:00", sabado: "cerrado", domingo: "cerrado" },
  working_hours_workshop: { lunes: "", martes: "", miercoles: "", jueves: "", viernes: "", sabado: "", domingo: "" },
  working_hours_parts: { lunes: "", martes: "", miercoles: "", jueves: "", viernes: "", sabado: "", domingo: "" },
};

function ImageUpload({ label, hint, value, onChange, testId, axiosAuth }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Formato no permitido. Usa JPG, PNG o WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximo 5MB.`);
      return;
    }

    // Show instant preview
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axiosAuth().post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      setPreview(null);
      toast.success("Imagen subida correctamente");
    } catch (err) {
      // Upload failed — revert preview, keep previous image
      setPreview(null);
      const msg = err.response?.data?.detail || "Error al subir imagen";
      toast.error(msg);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const displayUrl = preview || (value ? getImageUrl(value) : null);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
      {displayUrl ? (
        <div className="relative group">
          <img src={displayUrl} alt="" className="w-full h-32 object-cover rounded-sm border border-slate-200" />
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <div className="animate-spin w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs bg-white/90" onClick={() => fileRef.current?.click()} disabled={uploading}>Cambiar</Button>
            <Button variant="outline" size="sm" className="h-7 text-xs bg-white/90 text-red-600" onClick={() => { onChange(""); setPreview(null); }} disabled={uploading}><X className="w-3 h-3" /></Button>
          </div>
        </div>
      ) : (
        <div
          className="border-2 border-dashed border-slate-300 p-4 text-center hover:border-slate-400 transition-colors cursor-pointer rounded-sm"
          onClick={() => !uploading && fileRef.current?.click()}
        >
          <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1" />
          <span className="text-[10px] text-slate-500">{uploading ? "Subiendo..." : "Haz clic para subir imagen"}</span>
        </div>
      )}
      <Input value={value || ""} onChange={e => onChange(e.target.value)} placeholder="O pega una URL de imagen..." className="rounded-sm h-8 border-slate-200 text-xs" data-testid={testId} />
      <input ref={fileRef} type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={handleFile} />
    </div>
  );
}

function MapsUrlValidator({ url }) {
  if (!url) return (
    <div className="flex items-center gap-1.5 text-[10px] text-amber-600 bg-amber-50 p-1.5 rounded-sm">
      <AlertTriangle className="w-3 h-3" />
      Sin enlace configurado. El boton usara la direccion del centro como busqueda en Google Maps.
    </div>
  );
  const isValid = url.startsWith("http://") || url.startsWith("https://");
  if (!isValid) return (
    <div className="flex items-center gap-1.5 text-[10px] text-red-600 bg-red-50 p-1.5 rounded-sm">
      <AlertTriangle className="w-3 h-3" />
      URL no valida. Debe empezar por http:// o https://
    </div>
  );
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] text-green-600 bg-green-50 p-1.5 rounded-sm hover:underline">
      <ExternalLink className="w-3 h-3" /> Enlace valido — clic para probar
    </a>
  );
}

export default function CentersPage() {
  const { axiosAuth } = useAuth();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/centers`);
      setCenters(res.data);
    } catch { toast.error("Error al cargar centros"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setDialog(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", slug: c.slug || "", address: c.address || "",
      zip_code: c.zip_code || "", city: c.city || "", phone: c.phone || "",
      email: c.email || "", image_url: c.image_url || "",
      map_image_url: c.map_image_url || "", google_maps_url: c.google_maps_url || "",
      active: c.active !== false, order: c.order || 0,
      show_in_header: c.show_in_header !== false,
      header_order: c.header_order ?? c.order ?? 0,
      working_hours: c.working_hours || { ...emptyForm.working_hours },
      working_hours_workshop: c.working_hours_workshop || { ...emptyForm.working_hours_workshop },
      working_hours_parts: c.working_hours_parts || { ...emptyForm.working_hours_parts },
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.slug) { toast.error("Nombre y slug son obligatorios"); return; }
    if (saving) return;
    setSaving(true);
    try {
      if (editing) {
        await axiosAuth().put(`/centers/${editing.id}`, form);
        toast.success("Centro actualizado");
      } else {
        await axiosAuth().post("/centers", form);
        toast.success("Centro creado");
      }
      setDialog(false);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este centro?")) return;
    try {
      await axiosAuth().delete(`/centers/${id}`);
      toast.success("Centro eliminado");
      fetch();
    } catch { toast.error("Error al eliminar"); }
  };

  const updateHour = (day, val) => setForm(p => ({ ...p, working_hours: { ...p.working_hours, [day]: val } }));
  const updateHourWorkshop = (day, val) => setForm(p => ({ ...p, working_hours_workshop: { ...(p.working_hours_workshop || {}), [day]: val } }));
  const updateHourParts = (day, val) => setForm(p => ({ ...p, working_hours_parts: { ...(p.working_hours_parts || {}), [day]: val } }));

  return (
    <div className="space-y-5" data-testid="centers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="centers-title">Centros</h1>
          <p className="text-sm text-slate-500 mt-0.5">{centers.length} centros registrados</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-4" data-testid="new-center-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Centro
        </Button>
      </div>

      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Centro</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold hidden sm:table-cell">Direccion</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold hidden md:table-cell">Telefono</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold hidden lg:table-cell">Mapa</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Estado</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
              ) : centers.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No hay centros</TableCell></TableRow>
              ) : centers.map(c => (
                <TableRow key={c.id} data-testid={`center-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {(c.image_url || c.map_image_url) ? (
                        <img src={getImageUrl(c.image_url || c.map_image_url)} alt="" className="w-10 h-10 object-cover rounded-sm border" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center"><Building2 className="w-5 h-5 text-slate-300" /></div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{c.slug}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <p className="text-sm text-slate-700">{c.address}</p>
                    <p className="text-xs text-slate-500">{c.zip_code} {c.city}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-slate-700">{c.phone}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {c.google_maps_url ? (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-green-50 text-green-700 border-green-200">Configurado</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-amber-50 text-amber-700 border-amber-200">Auto</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-sm text-[10px] ${c.active ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                      {c.active ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="text-xs h-7" data-testid={`edit-center-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-xs h-7 text-red-600" data-testid={`delete-center-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="center-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">{editing ? "Editar Centro" : "Nuevo Centro"}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {/* Basic info */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Datos basicos</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-sm h-9 border-slate-200" data-testid="center-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Slug *</Label>
                  <Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/\s/g, '-') }))} placeholder="ej: mostoles" className="rounded-sm h-9 border-slate-200 font-mono" data-testid="center-slug" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Direccion</Label>
                  <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="rounded-sm h-9 border-slate-200" data-testid="center-address" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Codigo Postal</Label>
                  <Input value={form.zip_code} onChange={e => setForm(p => ({ ...p, zip_code: e.target.value }))} className="rounded-sm h-9 border-slate-200" data-testid="center-zip" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Ciudad</Label>
                  <Input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className="rounded-sm h-9 border-slate-200" data-testid="center-city" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Telefono</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="rounded-sm h-9 border-slate-200" data-testid="center-phone" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
                  <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="rounded-sm h-9 border-slate-200" data-testid="center-email" />
                </div>
              </div>
            </div>

            {/* Images */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5" /> Imagenes</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ImageUpload
                  label="Imagen del centro"
                  hint="Foto principal del centro (fachada, interior...)"
                  value={form.image_url}
                  onChange={v => setForm(p => ({ ...p, image_url: v }))}
                  testId="center-image-url"
                  axiosAuth={axiosAuth}
                />
                <ImageUpload
                  label="Imagen del mapa"
                  hint="Captura de mapa o preview de ubicacion"
                  value={form.map_image_url}
                  onChange={v => setForm(p => ({ ...p, map_image_url: v }))}
                  testId="center-map-image-url"
                  axiosAuth={axiosAuth}
                />
              </div>
            </div>

            {/* Maps link */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Enlace de mapa</h3>
              <div className="space-y-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">URL de Google Maps ("Como llegar")</Label>
                  <Input
                    value={form.google_maps_url}
                    onChange={e => setForm(p => ({ ...p, google_maps_url: e.target.value }))}
                    placeholder="https://maps.google.com/..."
                    className="rounded-sm h-9 border-slate-200 text-sm"
                    data-testid="center-google-maps-url"
                  />
                  <p className="text-[10px] text-slate-400">Abre Google Maps &gt; busca el centro &gt; Compartir &gt; copia el enlace y pegalo aqui.</p>
                </div>
                <MapsUrlValidator url={form.google_maps_url} />
              </div>
            </div>

            {/* State and order */}
            <div className="flex flex-wrap items-center gap-3">
              <Switch checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} data-testid="center-active" />
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Centro activo</Label>
              <Switch checked={form.show_in_header} onCheckedChange={v => setForm(p => ({ ...p, show_in_header: v }))} data-testid="center-show-in-header" />
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Mostrar horario en cabecera pública</Label>
              <div className="flex-1" />
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Orden cabecera</Label>
              <Input type="number" value={form.header_order} onChange={e => setForm(p => ({ ...p, header_order: parseInt(e.target.value) || 0 }))} className="w-16 rounded-sm h-9 border-slate-200" data-testid="center-header-order" />
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Orden</Label>
              <Input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="w-16 rounded-sm h-9 border-slate-200" data-testid="center-order" />
            </div>

            {/* Working hours - General (comercial) */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Horario comercial / general</h3>
              <p className="text-[10px] text-slate-400 mb-2">Este es el horario que se mostrará en la cabecera pública si está activado. Formato: <code className="bg-slate-100 px-1 rounded text-[10px]">07:00-18:00</code> o partido: <code className="bg-slate-100 px-1 rounded text-[10px]">07:00-14:00, 15:30-18:00</code></p>
              <div className="space-y-2">
                {DAYS.map(day => (
                  <div key={day} className="flex items-center gap-3">
                    <span className="w-24 text-xs text-slate-600 capitalize font-medium">{day}</span>
                    <Input value={form.working_hours?.[day] || ""} onChange={e => updateHour(day, e.target.value)}
                      placeholder="07:00-18:00 o cerrado" className="rounded-sm h-8 border-slate-200 text-xs flex-1" data-testid={`center-hours-${day}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Working hours - Workshop */}
            <details className="group">
              <summary className="cursor-pointer text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Horario taller (opcional)</summary>
              <p className="text-[10px] text-slate-400 mb-2">Si está vacío, no se mostrará. Si se rellena, podrá verse en la ficha pública del centro.</p>
              <div className="space-y-2">
                {DAYS.map(day => (
                  <div key={day} className="flex items-center gap-3">
                    <span className="w-24 text-xs text-slate-600 capitalize font-medium">{day}</span>
                    <Input value={form.working_hours_workshop?.[day] || ""} onChange={e => updateHourWorkshop(day, e.target.value)}
                      placeholder="07:00-18:00 o vacío" className="rounded-sm h-8 border-slate-200 text-xs flex-1" data-testid={`center-hours-workshop-${day}`} />
                  </div>
                ))}
              </div>
            </details>

            {/* Working hours - Parts */}
            <details className="group">
              <summary className="cursor-pointer text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Horario recambios (opcional)</summary>
              <p className="text-[10px] text-slate-400 mb-2">Si está vacío, no se mostrará.</p>
              <div className="space-y-2">
                {DAYS.map(day => (
                  <div key={day} className="flex items-center gap-3">
                    <span className="w-24 text-xs text-slate-600 capitalize font-medium">{day}</span>
                    <Input value={form.working_hours_parts?.[day] || ""} onChange={e => updateHourParts(day, e.target.value)}
                      placeholder="08:00-18:00 o vacío" className="rounded-sm h-8 border-slate-200 text-xs flex-1" data-testid={`center-hours-parts-${day}`} />
                  </div>
                ))}
              </div>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="rounded-sm text-xs uppercase" disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="center-save">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
